import axios, { AxiosPromise, AxiosRequestConfig, AxiosResponse } from 'axios'
import { cookieGet, cookieRemove, cookieSet } from '@/common/cookie'
import { Message } from 'element-ui'
import { isPlainObject } from 'lodash'
import qs from 'qs'
import store from '@/store'
import router from '@/router'

const busyProc = {
  reject: () => Promise.reject('Request rejected by interceptor'),
  cache: (oldPromise) => oldPromise,
}

interface catcherExtraConfig {
  onBusy?: keyof typeof busyProc,
}

// 记录和显示错误
function errorLog(info) {
  // 显示提示
  Message({
    message: info,
    type: 'error',
    duration: 5 * 1000
  })
}

// 成功
function success() {
  // 显示提示
  Message({
    message: 'success',
    type: 'success',
    duration: 3 * 1000
  })
}

const promiseCacher: Record<string, Record<string, {
  /** handle针对单例请求,即不会有多请求并行,保证listener回调的准确性无需额外处理 */
  handle?: Promise<AxiosResponse<any>>
  /** groupHandle针对并行请求,任意请求的开始都要视为loding开始,同时所有请求结束才视为loading结束 */
  groupHandle: Promise<AxiosResponse<any>>[]
  listener?: (loading: boolean) => void
}>> = {
  main: {

  },
  b2b: {

  },
  common: {

  },
  seller: {
  
  }
}

export type CacherKeys = "main" | "b2b" | "common" | "seller";

/**
 * 向axios注册特定回调,回调分别在请求发出,请求结束(不论完成还是报错)时以T/F为参数进行回调,回调函数暂时只允许一个
 * @param slot 接口所在的服务
 * @param url 接口url
 * @param cb 回调函数
 */
export function listenUrlLoading(slot: CacherKeys, url: string, cb: (boolean) => void) {
  if (!promiseCacher[url]) {
    promiseCacher[slot][url] = {
      groupHandle: [],
    };
  }

  promiseCacher[slot][url].listener = cb;
}

function createAxios(baseURL, cacherSlot: keyof typeof promiseCacher) {
  const instance = axios.create({
    method: 'POST',
    baseURL,
    timeout: 1000 * 180,
    withCredentials: true
  })

  /**
   * 请求拦截
   */
  instance.interceptors.request.use(config => {
    // config.headers['language'] = cookieGet('language') == 'zh-CN' ? 'sc' : 'eng'
    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json'
    }
    config.headers['token'] = cookieGet('token') || ''
    // 默认参数
    var defaults = {}
    // 防止缓存，GET请求默认带_t参数
    if (config.method === 'get' || config.method === 'delete' || (config.data && config.data.noQs)) {
      config.params = {
        ...config.params,
        ...{ '_t': new Date().getTime() }
      }
    }
    // if (isPlainObject(config.params)) {
    //   config.params = {
    //     ...defaults,
    //     ...config.params
    //   }
    // }
    if (isPlainObject(config.data)) {
      // config.data = {
      //   ...defaults,
      //   ...config.data
      // }

      if (/^application\/x-www-form-urlencoded/.test(config.headers['Content-Type'])) {
        console.log('yes');
        if (!config.data.noQs) {
          config.data = qs.stringify(config.data)
        }
      }
    }
    return config
  }, error => {
    return Promise.reject(error)
  })

  /**
   * 响应拦截
   */
  instance.interceptors.response.use(response => {
	  console.log('response',response)
    if (response.config.responseType === 'text' || response.config.responseType === 'blob' || response.data instanceof Blob) {
      return response
    }
    else if (response.data.code === "401" || response.data.code === "10001") {
      store.dispatch('d2admin/account/logout')
      return Promise.reject(response.data.message)
    }
	
    //用户登录过期处理
    else if (response.data.code !== "SYS00000") {
      if (response.data.message === "USER0999" && cookieGet('token') !== '') {
        errorLog('Login expired');
        cookieSet('token', '')
        router.replace('/login');
      }else if(response.data.code === "SYS00001") {
		  errorLog('Not Correct Command or Return Message.');
	  }else {
        errorLog(response.data.message);
      }
      //防止多次弹窗,这里给予cookie进行一次判定
      return Promise.reject(response.data.message);
    } else {
      //不要成功消息
      // if (response.config.method !== "get") {
      //   success()
      // }
      return response.data;
    }
  }, error => {
    errorLog(error.message)
    return Promise.reject(error)
  })

  // return instance;

  return {
    /**
     * 带缓存防抖的request,针对同一个url只允许顺序执行,否则执行期间内只能得到同一个promise对象
     */
    request<T = any>(config: AxiosRequestConfig & catcherExtraConfig): AxiosPromise<T> {
      if (promiseCacher[cacherSlot][config.url!] === undefined) {
        promiseCacher[cacherSlot][config.url!] = {
          groupHandle: []
        }
      }
      const cacherItem = promiseCacher[cacherSlot][config.url!];

      const oldPromise = cacherItem.handle
      if (oldPromise instanceof Promise) {
        if (config.onBusy! in busyProc) {
          return busyProc[config.onBusy!](oldPromise)
        }
        else {
          return busyProc.cache(oldPromise)
        }
      }

      if (cacherItem.listener) {
        cacherItem.listener(true);
      }

      cacherItem.handle = instance.request(config).finally(() => {
        // 这里不关心函数结果,只标记状态,只要状态不为pending,都开放下一个url的调用
        if (cacherItem.listener) {
          cacherItem.listener(false);
        }
        cacherItem.handle = undefined;
      });

      return cacherItem.handle;
    },
    /**
     * 正常的request,没有并发限制
     */
    multiRequest<T = any>(config: AxiosRequestConfig): AxiosPromise<T> {
      if (promiseCacher[config.url!] === undefined) {
        promiseCacher[cacherSlot][config.url!] = {
          groupHandle: []
        }
      }
      const cacherItem = promiseCacher[cacherSlot][config.url!];

      if (cacherItem.listener) {
        cacherItem.listener(true);
      }

      const handle = instance.request(config).finally(() => {
        if (cacherItem.listener && checkGroupHandle(cacherItem.groupHandle, handle)) {
          cacherItem.listener(false);
        }
      });

      cacherItem.groupHandle.push(handle);

      return handle;
    }
  }
}

/**
 * 内部检查函数,针对并发请求,当任意并发请求中的一个完成时,从集合中移除之;如果集合已空,则并发可视为完成
 */
function checkGroupHandle(groupHandle: Promise<AxiosResponse<any>>[], handle: Promise<AxiosResponse<any>>): boolean {
  const index = groupHandle.findIndex(v => v === handle);

  if (index !== -1) {
    groupHandle.splice(index, 1);
  }

  return groupHandle.length === 0;
}

// axios主实例
const { request: service, multiRequest: service_multi } = createAxios(process.env.VUE_APP_API, 'main');
const { request: service_b2b, multiRequest: service_b2b_multi } = createAxios(process.env.VUE_APP_API_B2B, 'b2b');
const { request: service_common, multiRequest: service_common_multi } = createAxios(process.env.VUE_APP_API_COMMON, 'common');
// axios副实例
const { request: service_seller, multiRequest: service_seller_multi } = createAxios(process.env.VUE_APP_API, 'seller');

export default service;
export { service, service_multi, service_b2b, service_b2b_multi, service_common, service_common_multi,service_seller };